import {useCallback, useState} from "react"

interface BaseBooleanState {
  setTrue: () => void
  setFalse: () => void
  setValue: (value: boolean) => void
  toggle: () => void
  value: boolean
}

interface BooleanStateWithHandler<T extends string> extends BaseBooleanState {
  handleEvent: (obj: {[K in T]: boolean}) => void
}

interface BooleanStateWithoutHandler extends BaseBooleanState {
  handleEvent: undefined
}

export function useBooleanState<T extends string>(
  initialValue: boolean,
  eventFieldName: T,
): BooleanStateWithHandler<T>
export function useBooleanState(
  initialValue: boolean,
): BooleanStateWithoutHandler

// Overloads for function types don't work with arrow functions, so
// disable the rule that automatically rewrites functions to arrow
// functions:
//
/* eslint-disable preferArrowFunctions/prefer-arrow-functions */
export function useBooleanState<T extends string>(
  initialValue: boolean,
  eventFieldName?: T,
): BooleanStateWithHandler<T> | BooleanStateWithoutHandler {
  const [value, setValue] = useState(initialValue)

  const setTrue = useCallback(() => setValue(true), [setValue])
  const setFalse = useCallback(() => setValue(false), [setValue])
  const toggle = useCallback(() => setValue((prev) => !prev), [setValue])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleEvent = useCallback(
    eventFieldName
      ? (obj: {[K in T]: boolean}) => {
          setValue(obj[eventFieldName])
        }
      : // We can't call useCallback conditionally, so call it with an
        // empty function when eventFieldName is not provided.
        () => {},
    [eventFieldName, setValue],
  )

  return {
    setTrue,
    setFalse,
    setValue,
    toggle,
    value,
    handleEvent: eventFieldName ? handleEvent : undefined,
  } as BooleanStateWithHandler<T> | BooleanStateWithoutHandler
}
/* eslint-enable preferArrowFunctions/prefer-arrow-functions */
